import { lazy } from "react";
import { createBrowserRouter } from "react-router-dom";

import ErrorElement from "@/components/ErrorElement";
import ProtectedRoute from "@/components/ProtectedRoute";
import { ROUTES } from "@/constants/routes";
import {
  InstallationItemBreadcrumb,
  PowerStationBreadcrumb,
  SensorDetailBreadcrumb,
} from "@/containers/AppBreadcrumb";
import BreadcrumbLink from "@/containers/BreadcrumbLink";
import AuthenticationLayout from "@/layout/AuthenticationLayout";
import MainLayout from "@/layout/MainLayout";
import OsosOnboardingLayout from "@/layout/OsosOnboardingLayout";
import Page404 from "@/pages/Page404";

// Auth Pages
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const RegisterPage = lazy(() => import("@/pages/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ActivateUserPage = lazy(() => import("@/pages/ActivateUserPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));

// Pages
const OsosPage = lazy(() => import("@/pages/OsosPage"));
const OsosDetailPage = lazy(() => import("@/pages/OsosDetailsPage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const AdministrationPage = lazy(() => import("@/pages/Administration"));
const DocumentsPage = lazy(() => import("@/pages/DocumentsPage"));
const BudgetPlanningPage = lazy(() => import("@/pages/BudgetPlanningPage"));
const BillingPredictionPage = lazy(
  () => import("@/pages/BillingPredictionPage"),
);
const BillingsPage = lazy(() => import("@/pages/BillingsPage"));
const OsosOnboardingPage = lazy(() => import("@/pages/OsosOnboardingPage"));
const SensorsPage = lazy(() => import("@/pages/SensorsPage"));
const AlarmsPage = lazy(() => import("@/pages/AlarmsPage"));
const GenerationPage = lazy(() => import("@/pages/GenerationPage"));
const GenerationDetailPage = lazy(() => import("@/pages/GenerationDetailPage"));
const SensorDetailPage = lazy(() => import("@/pages/SensorDetailPage"));
const SensorComparePage = lazy(() => import("@/pages/SensorComparePage"));
const ReportsPage = lazy(() => import("@/pages/ReportsPage"));
const SustainabilityPage = lazy(() => import("@/pages/SustainabilityPage"));
const McpPredictionPage = lazy(() => import("@/pages/McpPredictionPage"));

export const routes = [
  {
    element: <AuthenticationLayout />,
    errorElement: <ErrorElement />,
    children: [
      {
        path: ROUTES.LOGIN,
        element: <LoginPage />,
      },
      {
        path: ROUTES.REGISTER,
        element: <RegisterPage />,
      },
      {
        path: ROUTES.FORGOT_PASSWORD,
        element: <ForgotPasswordPage />,
      },
      {
        path: ROUTES.ACTIVATE_USER,
        element: <ActivateUserPage />,
      },
      {
        path: ROUTES.RESET_PASSWORD,
        element: <ResetPasswordPage />,
      },
    ],
  },
  {
    element: <ProtectedRoute />,
    errorElement: <ErrorElement />,
    children: [
      {
        path: ROUTES.DASHBOARD,
        element: <MainLayout />,
        children: [
          {
            element: <DashboardPage />,
            index: true,
          },
          {
            path: ROUTES.OSOS,
            element: <OsosPage />,
            breadcrumb: BreadcrumbLink,
            props: {
              title: "osos",
              to: ROUTES.OSOS,
            },
          },
          {
            path: `${ROUTES.OSOS}/:installationRef`,
            element: <OsosDetailPage />,
            breadcrumb: () => <InstallationItemBreadcrumb />,
          },
          {
            path: ROUTES.ADMINISTRATION,
            element: <AdministrationPage />,
          },
          {
            path: ROUTES.BILLINGS,
            element: <BillingsPage />,
          },
          {
            path: ROUTES.DOCUMENTS,
            element: <DocumentsPage />,
          },
          {
            path: ROUTES.BILLING_PREDICTION,
            element: <BillingPredictionPage />,
          },
          {
            path: ROUTES.BUDGET_PLANNING,
            element: <BudgetPlanningPage />,
          },
          {
            path: ROUTES.SENSORS,
            element: <SensorsPage />,
            breadcrumb: BreadcrumbLink,
            props: {
              title: "sensors",
              to: ROUTES.SENSORS,
            },
          },
          {
            path: `${ROUTES.SENSORS}/:deviceId`,
            element: <SensorDetailPage />,
            breadcrumb: () => <SensorDetailBreadcrumb />,
          },
          {
            path: ROUTES.ALARMS,
            element: <AlarmsPage />,
          },
          {
            path: ROUTES.REPORTS,
            element: <ReportsPage />,
          },
          {
            path: ROUTES.GENERATION,
            element: <GenerationPage />,
            breadcrumb: BreadcrumbLink,
            props: {
              title: "generation",
              to: ROUTES.GENERATION,
            },
          },
          {
            path: `${ROUTES.GENERATION}/:stationId`,
            element: <GenerationDetailPage />,
            breadcrumb: PowerStationBreadcrumb,
          },
          {
            path: ROUTES.SENSOR_COMPARE,
            element: <SensorComparePage />,
          },
          {
            path: ROUTES.SUSTAINABILITY,
            element: <SustainabilityPage />,
          },
          {
            path: ROUTES.MCP_PREDICTION,
            element: <McpPredictionPage />,
          },
        ],
      },
      {
        path: ROUTES.OSOS_ONBOARDING,
        element: <OsosOnboardingLayout />,
        children: [
          {
            element: <OsosOnboardingPage />,
            index: true,
          },
        ],
      },
    ],
  },
  {
    path: "*",
    element: <Page404 />,
  },
];

export default createBrowserRouter(routes);
